import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import notifications from '../../notifications';
import { getProgramRoster, removeKidFromProgram } from '../../api/admin';
import AddKidForm from './AddKidForm';
import EditKidModal from './EditKidModal';
import ProgramCustomEmail from './ProgramCustomEmail';
import './RosterTab.css';

const RosterTab = ({ programId, programOrg }) => {
  const [roster, setRoster] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingKid, setEditingKid] = useState(null);
  const [expandedKid, setExpandedKid] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [removeForm, setRemoveForm] = useState({
    futureOnly: false,
    removeKid: false
  });

  useEffect(() => {
    const fetchRoster = async () => {
      setIsLoading(true);
      try {
        const response = await getProgramRoster(programId);
        if (response.data) {
          setRoster(response.data.roster || []);
        } else {
          notifications.notifyError('Failed to load roster');
        }
      } catch (error) {
        notifications.notifyError('Failed to load roster');
      } finally {
        setIsLoading(false);
      }
    };
    fetchRoster();
  }, [programId]);

  const loadRoster = async () => {
    setIsLoading(true);
    try {
      const response = await getProgramRoster(programId);
      if (response.data) {
        setRoster(response.data.roster || []);
      } else {
        notifications.notifyError('Failed to load roster');
      }
    } catch (error) {
      notifications.notifyError('Failed to load roster');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSuccess = () => {
    loadRoster();
    setShowAddForm(false);
  };

  const handleEditSuccess = () => {
    loadRoster();
    setEditingKid(null);
  };

  const handleExpandRemove = (kidId) => {
    if (expandedKid === kidId) {
      setExpandedKid(null);
    } else {
      setExpandedKid(kidId);
      setRemoveForm({ futureOnly: false, removeKid: false });
    }
  };

  const handleRemoveSubmit = async (kid) => {
    setIsSubmitting(true);
    try {
      const response = await removeKidFromProgram(
        kid.kidId,
        programId,
        removeForm.futureOnly,
        removeForm.removeKid
      );
      if (!response.error) {
        notifications.notifySuccess('Kid removed from program');
        setExpandedKid(null);
        loadRoster();
      } else {
        notifications.notifyError('Failed to remove kid');
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatAge = (dob) => {
    if (!dob) return '-';
    const birthDate = DateTime.fromISO(dob);
    const now = DateTime.now();
    const diff = now.diff(birthDate, ['years', 'months']);
    const years = Math.floor(diff.years);
    const months = Math.floor(diff.months);
    return `${years}y ${months}mo`;
  };

  const formatDate = (dob) => {
    if (!dob) return '-';
    return DateTime.fromISO(dob).toFormat('MM/dd/yyyy');
  };

  if (isLoading) {
    return <div className="roster-loading">Loading roster...</div>;
  }

  return (
    <div className="roster-tab">
      <div className="roster-header">
        <h3>Roster ({roster.length} kids)</h3>
        <button
          className="add-kid-btn"
          onClick={() => setShowAddForm(true)}
        >
          + Add Kid
        </button>
      </div>

      {showAddForm && (
        <AddKidForm
          programId={programId}
          programOrg={programOrg}
          onSuccess={handleAddSuccess}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      <div className="roster-table-container">
        <table className="roster-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Age</th>
              <th>DOB</th>
              <th>Gender</th>
              <th>Contact Name</th>
              <th>Contact Phone</th>
              <th>Contact Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roster.map((kid) => (
              <React.Fragment key={kid.kidId}>
                <tr>
                  <td>{kid.firstName} {kid.lastName}</td>
                  <td>{formatAge(kid.dob)}</td>
                  <td>{formatDate(kid.dob)}</td>
                  <td>{kid.gender || '-'}</td>
                  <td>{kid.contactFirstName} {kid.contactLastName}</td>
                  <td>{kid.contactPhone || '-'}</td>
                  <td>{kid.contactEmail || '-'}</td>
                  <td className="actions-cell">
                    <button
                      className="action-btn edit"
                      onClick={() => setEditingKid(kid)}
                    >
                      Edit
                    </button>
                    <button
                      className={`action-btn remove ${expandedKid === kid.kidId ? 'active' : ''}`}
                      onClick={() => handleExpandRemove(kid.kidId)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
                {expandedKid === kid.kidId && (
                  <tr className="action-row">
                    <td colSpan="8">
                      <div className="inline-form remove-form">
                        <h4>Remove {kid.firstName} {kid.lastName}</h4>
                        <div className="checkbox-row">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={removeForm.futureOnly}
                              onChange={(e) => setRemoveForm({ ...removeForm, futureOnly: e.target.checked })}
                            />
                            Future sessions only (keep in program roster)
                          </label>
                        </div>
                        <div className="checkbox-row">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={removeForm.removeKid}
                              onChange={(e) => setRemoveForm({ ...removeForm, removeKid: e.target.checked })}
                            />
                            Delete kid from database entirely
                          </label>
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="cancel-btn"
                            onClick={() => setExpandedKid(null)}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="submit-btn danger"
                            onClick={() => handleRemoveSubmit(kid)}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Removing...' : 'Remove from Program'}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {roster.length === 0 && (
          <div className="empty-roster">No kids enrolled in this program</div>
        )}
      </div>

      <div className="roster-actions">
        <button
          className="send-email-btn"
          onClick={() => setShowEmailForm(!showEmailForm)}
          disabled={roster.length === 0}
        >
          {showEmailForm ? 'Cancel Email' : 'Send Custom Email'}
        </button>
      </div>

      {showEmailForm && (
        <div className="email-form-container">
          <div className="email-form-header">
            <h4>Send Custom Email</h4>
          </div>
          <p className="email-info">This will send an email to all {roster.length} enrolled kids.</p>
          <ProgramCustomEmail
            programId={programId}
            handleClose={() => setShowEmailForm(false)}
          />
        </div>
      )}

      {editingKid && (
        <EditKidModal
          kid={editingKid}
          onSuccess={handleEditSuccess}
          onClose={() => setEditingKid(null)}
        />
      )}
    </div>
  );
};

export default RosterTab;
